import { useEffect, useState } from "react";
import { Linkedin, Copy, Check, ExternalLink, Mail, MessageCircle, Link2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

// Reusable share dialog with a ready-to-post, editable LinkedIn text.
// LinkedIn no longer prefills text from share links reliably, so we always
// copy the text to the clipboard and then open the LinkedIn composer.
export const ShareDialog = ({ open, onOpenChange, url, defaultText, title }) => {
  const [text, setText] = useState(defaultText || "");
  const [copied, setCopied] = useState(false);
  const enc = encodeURIComponent;
  const full = `${text}\n\n${url}`;

  useEffect(() => {
    if (open) setText(defaultText || "");
  }, [open, defaultText]);

  const copy = async (silent) => {
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      if (!silent) toast.success("Post-Text kopiert – jetzt in LinkedIn einfügen.");
      setTimeout(() => setCopied(false), 2500);
      return true;
    } catch {
      if (!silent) toast.error("Kopieren nicht möglich – Text bitte manuell markieren.");
      return false;
    }
  };

  const shareLinkedIn = async () => {
    await copy(true);
    toast.success("Text kopiert – im LinkedIn-Fenster mit Strg/Cmd + V einfügen.");
    window.open(
      `https://www.linkedin.com/feed/?shareActive=true&text=${enc(full)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const quick = [
    { id: "mail", icon: Mail, label: "E-Mail",
      href: `mailto:?subject=${enc(title)}&body=${enc(full)}` },
    { id: "whatsapp", icon: MessageCircle, label: "WhatsApp",
      href: `https://wa.me/?text=${enc(full)}` },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-none bg-[#0d0d0d] border-white/15 text-white sm:max-w-lg"
        data-testid="share-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2 text-white">
            <Linkedin className="h-5 w-5 text-brand-orange" /> Auf LinkedIn teilen
          </DialogTitle>
          <DialogDescription className="text-white/55 text-sm">
            Fertiger Post-Text – bei Bedarf anpassen, kopieren und in LinkedIn einfügen.
          </DialogDescription>
        </DialogHeader>

        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={9}
          data-testid="share-post-text"
          className="rounded-none bg-[#111] border-white/15 text-white text-sm leading-relaxed focus-visible:ring-1 focus-visible:ring-brand-orange resize-none"
        />
        <p className="text-xs text-white/40 -mt-1">Der Link wird beim Kopieren automatisch angehängt.</p>

        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={() => copy(false)}
            data-testid="share-copy-btn"
            variant="outline"
            className="h-11 rounded-none border-white/15 text-white/80 hover:border-brand-orange hover:text-brand-orange bg-transparent uppercase tracking-wider text-xs"
          >
            {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
            {copied ? "Kopiert" : "Text kopieren"}
          </Button>
          <Button
            onClick={shareLinkedIn}
            data-testid="share-linkedin-btn"
            className="h-11 rounded-none bg-brand-orange text-black hover:bg-white font-bold uppercase tracking-wider text-xs transition-colors"
          >
            <ExternalLink className="mr-2 h-4 w-4" /> LinkedIn öffnen
          </Button>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <button
            onClick={async () => {
              try { await navigator.clipboard.writeText(url); toast.success("Link kopiert."); }
              catch { toast.error("Kopieren nicht möglich."); }
            }}
            data-testid="share-copy-link"
            className="flex-1 flex items-center justify-center gap-2 h-10 border border-white/15 text-white/70 hover:border-brand-orange hover:text-brand-orange transition-colors text-xs uppercase tracking-wider"
          >
            <Link2 className="h-4 w-4" /> Nur Link
          </button>
          {quick.map((q) => (
            <a
              key={q.id}
              href={q.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`share-${q.id}`}
              className="flex-1 flex items-center justify-center gap-2 h-10 border border-white/15 text-white/70 hover:border-brand-orange hover:text-brand-orange transition-colors text-xs uppercase tracking-wider"
            >
              <q.icon className="h-4 w-4" /> {q.label}
            </a>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;
